import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";

const OWNER_KEY = "welfare-push-owner";
let pending: Promise<unknown> = Promise.resolve();

function serialized<T>(operation: () => Promise<T>): Promise<T> {
  const next = pending.then(() => navigator.locks
    ? navigator.locks.request("welfare-push", operation)
    : operation());
  pending = next.catch(() => undefined);
  return next;
}


async function request(path: string, method = "GET", body?: unknown) {
  const response = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.success === false) {
    throw new Error(data.error || "Unable to update notifications. Please try again.");
  }
  return data;
}

async function checkUser(userId: string) {
  const data = await request("/api/push/subscription");
  if (data.codicePersona !== userId) {
    throw new Error("Your account has changed. Reload this page before changing notifications.");
  }
}

async function unsubscribe(subscription: PushSubscription) {
  if (!await subscription.unsubscribe()) {
    throw new Error("Unable to unsubscribe this browser. Please try again.");
  }
  localStorage.removeItem(OWNER_KEY);
}

async function detach() {
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = await registration?.pushManager.getSubscription();
  if (subscription) {
    await request("/api/push/subscription", "DELETE", { endpoint: subscription.endpoint });
    await unsubscribe(subscription);
  } else {
    localStorage.removeItem(OWNER_KEY);
  }
}

export async function disablePushBeforeLogout() {
  if (window.isSecureContext && "serviceWorker" in navigator && "PushManager" in window) await serialized(detach);
}

export function PushNotifications({ userId }: { userId: string }) {
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [permission, setPermission] = useState<NotificationPermission>(
    "Notification" in window ? Notification.permission : "default",
  );
  const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const standalone = window.matchMedia("(display-mode: standalone)").matches ||
    Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
  const needsHomeScreen = ios && !standalone;
  const available = window.isSecureContext && "serviceWorker" in navigator &&
    "PushManager" in window && "Notification" in window && !needsHomeScreen;

  useEffect(() => {
    let active = true;
    setEnabled(false);
    setBusy(true);
    setError("");
    serialized(async () => {
      if (!available || !active) return false;
      const registration = await navigator.serviceWorker.getRegistration("/");
      const subscription = await registration?.pushManager.getSubscription();
      if (!subscription || !active) return false;
      // An unknown or different owner must never inherit an existing endpoint.
      if (localStorage.getItem(OWNER_KEY) !== userId) {
        await unsubscribe(subscription);
        return false;
      }
      if (Notification.permission !== "granted") return false;
      await checkUser(userId);
      if (!active) return false;
      await request("/api/push/subscription", "POST", subscription.toJSON());
      return true;
    }).then(value => {
      if (active) setEnabled(value);
    }).catch(reason => {
      if (active) setError(reason instanceof Error ? reason.message : "Unable to check notifications.");
    }).finally(() => {
      if (active) setBusy(false);
    });
    return () => { active = false; };
  }, [userId, available]);

  async function enable() {
    setBusy(true);
    setError("");
    try {
      // Ask directly in the click handler: mobile browsers require a user gesture.
      const granted = await Notification.requestPermission();
      setPermission(granted);
      if (granted !== "granted") return;
      await serialized(async () => {
        await checkUser(userId);
        await navigator.serviceWorker.register("/service-worker.js");
        const registration = await navigator.serviceWorker.ready;
        let subscription = await registration.pushManager.getSubscription();
        if (subscription && localStorage.getItem(OWNER_KEY) !== userId) {
          await unsubscribe(subscription);
          subscription = null;
        }
        if (!subscription) {
          const { publicKey } = await request("/api/push/key");
          if (typeof publicKey !== "string" || !publicKey) throw new Error("Notifications are not configured yet.");
          const base64 = publicKey.replace(/-/g, "+").replace(/_/g, "/");
          const decoded = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
          const applicationServerKey = Uint8Array.from(decoded, character => character.charCodeAt(0));
          subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
          try {
            localStorage.setItem(OWNER_KEY, userId);
          } catch (reason) {
            await subscription.unsubscribe();
            throw reason;
          }
        }
        await checkUser(userId);
        await request("/api/push/subscription", "POST", subscription.toJSON());
      });
      setEnabled(true);
    } catch (reason) {
      setEnabled(false);
      setError(reason instanceof Error ? reason.message : "Unable to enable notifications.");
    } finally {
      setBusy(false);
    }
  }

  async function disable() {
    setBusy(true);
    setError("");
    try {
      await serialized(async () => {
        await checkUser(userId);
        await detach();
      });
      setEnabled(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Unable to disable notifications.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-w-0 space-y-2 rounded-lg border px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div className={`h-2 w-2 shrink-0 rounded-full ${enabled ? "bg-green-500" : "bg-muted-foreground"}`} aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-xs font-medium">Code and session notifications</p>
            <p className="text-xs text-muted-foreground">
              {enabled ? "Notifications enabled for generated codes and Polimi sessions expiring in 2 days or 1 day." : "Get code alerts and Polimi session reminders 2 days and 1 day before expiry."}
            </p>
          </div>
        </div>
        {available && (
          <Button
            variant="outline"
            size="sm"
            className="h-8 shrink-0 px-2 text-xs"
            onClick={enabled ? disable : enable}
            disabled={busy || (!enabled && permission === "denied")}
            aria-label={enabled ? "Disable code and session notifications" : "Enable code and session notifications"}
            aria-pressed={enabled}
          >
            {busy ? "Updating…" : enabled ? "Disable" : "Enable"}
          </Button>
        )}
      </div>
      {needsHomeScreen ? (
        <p className="text-xs text-muted-foreground">In Safari, tap Share → Add to Home Screen, then enable notifications in the app.</p>
      ) : !available ? (
        <p className="text-xs text-muted-foreground">Requires a supported browser and HTTPS.</p>
      ) : permission === "denied" ? (
        <p className="text-xs text-muted-foreground">Allow notifications in browser settings, then reload.</p>
      ) : null}
      {error && <p className="text-xs text-destructive" role="alert">{error}</p>}
    </div>
  );
}
